/**
 * Un numéro de facture, cliquable quand le PDF existe.
 *
 * Les écrans du rapprochement affichent des numéros de facture partout, et
 * l'administration ne pouvait pas les ouvrir : il fallait aller les chercher
 * dans un autre écran, en recopiant le numéro.
 *
 * ## Le repli n'est pas un détail
 *
 * 3 656 factures sur 3 689 portent un PDF ; 33 n'en ont pas. Celles-là
 * s'affichent en texte simple plutôt qu'en lien — un lien mort qui ouvre un
 * onglet vide apprend moins qu'un numéro qu'on voit tout de suite ne pas être
 * cliquable.
 *
 * `rel="noreferrer"` avec `target="_blank"` : sans lui, la page ouverte reçoit
 * une référence vers celle-ci. C'est la précaution standard, et elle ne coûte
 * rien.
 */

import { ExternalLink } from 'lucide-react';
import clsx from 'clsx';

export default function LienFacture({
  invoiceNumber,
  pdfUrl,
  ton = 'neutre',
}: {
  invoiceNumber: string;
  pdfUrl?: string | null;
  /**
   * `succes` marque une facture effectivement rapprochée, dans le journal.
   * `neutre` partout ailleurs.
   */
  ton?: 'neutre' | 'succes';
}) {
  const classeTexte = ton === 'succes' ? 'text-green-900' : 'text-gray-900';

  if (!pdfUrl) {
    return (
      <span
        title="Aucun PDF enregistré pour cette facture"
        className={clsx('font-mono text-xs', classeTexte)}
      >
        {invoiceNumber}
      </span>
    );
  }

  return (
    <a
      href={pdfUrl}
      target="_blank"
      rel="noreferrer"
      title={`Ouvrir la facture ${invoiceNumber}`}
      className={clsx(
        'inline-flex items-center gap-1 font-mono text-xs underline decoration-dotted underline-offset-2 hover:decoration-solid',
        ton === 'succes' ? 'text-green-900 hover:text-green-700' : 'text-indigo-700 hover:text-indigo-900',
      )}
    >
      {invoiceNumber}
      <ExternalLink className="h-3 w-3 shrink-0" />
    </a>
  );
}
